import React, { FunctionComponent, ReactNode } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { ClientDetailsSidePanelTextTone, getClientDetailsSidePanelTextColor } from './ClientDetailsSidePanelText'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { tID } from '../../utils/utils'

export type ClientDetailsSidePanelDetail = {
  id: string
  label: ReactNode
  value: ReactNode
  valueTone?: ClientDetailsSidePanelTextTone
  valueEmphasis?: 'default' | 'prominent'
  accessibilityLabel?: string
  labelAccessory?: ReactNode
  sublabel?: ReactNode
}

export type ClientDetailsSidePanelDetailsProps = {
  items: readonly ClientDetailsSidePanelDetail[]
  showTopDivider?: boolean
  emptyContent?: ReactNode
  testIDPrefix: string
}

const Details = styled.View<{ showTopDivider: boolean }>(({ theme, showTopDivider }) => ({
  width: '100%',
  gap: theme.spacing['8px'],
  ...(showTopDivider && {
    marginTop: theme.spacing['16px'],
    paddingTop: theme.spacing['16px'],
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderDefault,
    borderStyle: 'solid',
  }),
}))

const DetailRow = styled.View(({ theme }) => ({
  width: '100%',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing['8px'],
}))

const LabelContainer = styled.View({
  flex: 1,
  minWidth: 'auto',
})

const LabelRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const DetailLabel = styled(BodyText)({
  flexShrink: 1,
  minWidth: 0,
})

const DetailSublabel = styled(BodyText)({
  flexShrink: 1,
  minWidth: 0,
})

const DetailValue = styled(BodyText)({
  flexShrink: 1,
  minWidth: 0,
  textAlign: 'right',
})

export const ClientDetailsSidePanelDetails: FunctionComponent<ClientDetailsSidePanelDetailsProps> = ({
  items,
  showTopDivider = true,
  emptyContent,
  testIDPrefix,
}) => {
  const theme = useTheme()

  if (items.length === 0) {
    return emptyContent ? <>{emptyContent}</> : null
  }

  return (
    <Details showTopDivider={showTopDivider} testID={tID(testIDPrefix)}>
      {items.map(
        ({ id, label, value, valueTone, valueEmphasis = 'default', accessibilityLabel, labelAccessory, sublabel }) => (
          <DetailRow
            key={id}
            accessible={Boolean(accessibilityLabel)}
            accessibilityLabel={accessibilityLabel}
            testID={tID(`${testIDPrefix}-${id}`)}
          >
            <LabelContainer>
              <LabelRow>
                <DetailLabel
                  testID={tID(`${testIDPrefix}-${id}-label`)}
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textSecondary}
                  text={label}
                  regular
                />
                {labelAccessory}
              </LabelRow>
              {sublabel && (
                <DetailSublabel
                  testID={tID(`${testIDPrefix}-${id}-sublabel`)}
                  size={BodyTextSize.CAPTION}
                  color={theme.colors.textSecondary}
                  text={sublabel}
                  regular
                />
              )}
            </LabelContainer>
            <DetailValue
              testID={tID(`${testIDPrefix}-${id}-value`)}
              size={valueEmphasis === 'prominent' ? BodyTextSize.DEFAULT : BodyTextSize.SMALL}
              color={getClientDetailsSidePanelTextColor(valueTone, theme)}
              text={value}
              regular
            />
          </DetailRow>
        ),
      )}
    </Details>
  )
}
